import { Fragment } from 'react';
import type { RigaLink } from '@masamune/shared';
import { Icon } from '../../components/ui/Icon';
import { MActions, MRow, type MAzione } from '../../components/mobile/MobileKit';
import { clicCheApre, iconaLink, linkContatto, linkInterno, recapitoDi } from './riquadriTesto';
import './riquadri.css';

/**
 * Le cose nominate nella risposta di MUNE, con il link alla loro pagina (Fase 2, fetta 2B
 * «Chiedi», 29/10/2026; os-planning/ai/fase-2-progetto.md, «Righe con link»). Arrivano una
 * volta, a fine risposta (evento `righe`): il titolo e la riga sotto vengono dal dato, mai
 * dal modello, e gli indirizzi li costruisce il server dagli id letti. Il testo del modello
 * resta testo: i link stanno solo qui.
 *
 * Computer: elenco compatto, icona come nel menu, titolo cliccabile (la riga si clicca
 * tutta e la pagina si apre col pannello aperto), sotto in grigio; il referente ha a
 * destra telefono e mail. Telefono: MRow con la freccia, e sotto il referente i tasti
 * «Chiama» e «Scrivi»; un tocco chiude il pannello e apre la pagina (`onLink` del pannello).
 */
export function RigheLink({ righe, mobile, onLink }: {
  righe: RigaLink[];
  mobile: boolean;
  onLink: (href: string) => void;
}): JSX.Element {
  if (!righe.length) return <></>;
  return mobile ? <RigheTelefono righe={righe} onLink={onLink} /> : <RigheComputer righe={righe} onLink={onLink} />;
}

/** Chiama e Scrivi del referente, solo coi tel: e mailto: veri. */
function contattiDi(r: RigaLink): Array<{ etichetta: 'Chiama' | 'Scrivi'; href: string }> {
  return (r.azioni ?? []).flatMap((a) => {
    const href = linkContatto(a.href);
    return href ? [{ etichetta: a.etichetta, href }] : [];
  });
}

const chiaveDi = (r: RigaLink, i: number) => `${r.tipo}-${r.id}-${i}`;

// ════════════════════════════════════════════════════════════════════
// Computer
// ════════════════════════════════════════════════════════════════════

function RigheComputer({ righe, onLink }: { righe: RigaLink[]; onLink: (href: string) => void }) {
  return (
    <ul className="rl" aria-label="Pagine nominate nella risposta">
      {righe.map((r, i) => <RigaComputer key={chiaveDi(r, i)} r={r} onLink={onLink} />)}
    </ul>
  );
}

function RigaComputer({ r, onLink }: { r: RigaLink; onLink: (href: string) => void }) {
  const href = linkInterno(r.href);
  const apri = href ? clicCheApre(href, onLink) : undefined;
  const contatti = contattiDi(r);
  return (
    <li className={href ? 'rl-riga rl-riga--link' : 'rl-riga'} onClick={apri}>
      <Icon name={iconaLink(r.tipo)} size={14} className="rl-icona" />
      <div className="rl-testo">
        {href
          ? <a className="rl-titolo" href={href} onClick={apri}>{r.titolo}</a>
          : <span className="rl-titolo">{r.titolo}</span>}
        {r.sotto && <span className="rl-sotto">{r.sotto}</span>}
      </div>
      {contatti.length > 0 && (
        <div className="rl-azioni">
          {contatti.map((c) => {
            const chiama = c.etichetta === 'Chiama';
            const recapito = recapitoDi(c.href);
            return (
              <a
                key={c.etichetta}
                className="rl-azione"
                href={c.href}
                // Al passaggio il numero o l'indirizzo: dal computer il tel: spesso non chiama niente.
                title={chiama ? `Chiama ${recapito}` : `Scrivi a ${recapito}`}
                aria-label={chiama ? `Chiama ${r.titolo}` : `Scrivi a ${r.titolo}`}
                // Il tel: e il mailto: li apre il browser: il clic non arriva alla riga, che aprirebbe la pagina.
                onClick={(e) => e.stopPropagation()}
              >
                <Icon name={chiama ? 'phone' : 'mail'} size={14} />
              </a>
            );
          })}
        </div>
      )}
    </li>
  );
}

// ════════════════════════════════════════════════════════════════════
// Telefono
// ════════════════════════════════════════════════════════════════════

function RigheTelefono({ righe, onLink }: { righe: RigaLink[]; onLink: (href: string) => void }) {
  return (
    <div className="mk-list rl-m">
      {righe.map((r, i) => {
        const href = linkInterno(r.href);
        const azioni: MAzione[] = contattiDi(r).map((c) => ({
          label: c.etichetta,
          icon: c.etichetta === 'Chiama' ? 'phone' : 'mail',
          href: c.href,
        }));
        return (
          <Fragment key={chiaveDi(r, i)}>
            <MRow
              icon={iconaLink(r.tipo)}
              title={r.titolo}
              sub={r.sotto ?? undefined}
              subWrap
              onClick={href ? () => onLink(href) : undefined}
            />
            {azioni.length > 0 && (
              <div className="rl-m-azioni">
                <MActions azioni={azioni} />
              </div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
}
